import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatest } from 'lib/hooks/use-latest';
import type { Pieces } from './unit-shape-editing';

// Matches the Unit Builder mock (EditSlotMap.tsx): deep enough that a long
// editing session never loses reachable history, bounded so it can't grow
// without limit.
const HISTORY_LIMIT = 100;

/**
 * State restored alongside the boundary on undo.
 *
 * The Unit Builder composes its boundary from the survey rows, so a row added or
 * removed is a boundary change — one action, and undo has to reverse both halves
 * or the row list and the shape disagree. Optional, so the other consumer
 * (`type-curve-map.tsx`) is unaffected.
 */
export interface UnitShapeEditCompanion<T> {
  /** Read when a snapshot is pushed. */
  value: T;
  /** Applied when that snapshot is popped. */
  restore: (value: T) => void;
}

export interface UnitShapeEditState {
  /** Current working boundary. */
  pieces: Pieces;
  /** Replace the boundary (the editor's `onChange`). */
  setPieces: (pieces: Pieces) => void;
  /**
   * Snapshot the current boundary before a change (the editor's
   * `onBeforeChange`). A drag gesture calls this once, on drag start, so the
   * whole drag undoes as one step.
   */
  pushHistory: () => void;
  /** Step back one snapshot. */
  undo: () => void;
  canUndo: boolean;
  /** True once the boundary has been changed from the seed. */
  dirty: boolean;
}

/**
 * Working-copy state for unit boundary editing, with client-side undo.
 *
 * Undo is deliberately local and issues no request — the spec is explicit that
 * the panel's Undo is a client discard. Passing a new `seed` (a different unit,
 * or leaving and re-entering edit mode) discards the working copy and clears
 * history.
 *
 * @param {Pieces | null} seed - Starting boundary, or `null` when not editing.
 * @param {UnitShapeEditCompanion} companion - State to snapshot and restore with
 *   the boundary, so one undo reverses one action.
 * @return {UnitShapeEditState} Working boundary plus history controls.
 */
export function useUnitShapeEdit<T = never>(
  seed: Pieces | null,
  companion?: UnitShapeEditCompanion<T>
): UnitShapeEditState {
  const [pieces, setPiecesState] = useState<Pieces>(seed ?? []);
  const [canUndo, setCanUndo] = useState(false);
  const [dirty, setDirty] = useState(false);
  const historyRef = useRef<{ pieces: Pieces; companion?: T }[]>([]);
  const piecesRef = useLatest(pieces);
  // Read through a ref so push/undo keep one identity as the companion changes.
  const companionRef = useLatest(companion);

  useEffect(() => {
    historyRef.current = [];
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a new seed (another unit, or re-entering edit mode) discards the working copy; `seed` is a prop, and keying each caller's editor on it would remount its map.
    setPiecesState(seed ?? []);
    setCanUndo(false);
    setDirty(false);
  }, [seed]);

  const setPieces = useCallback((next: Pieces) => {
    setPiecesState(next);
    setDirty(true);
  }, []);

  const pushHistory = useCallback(() => {
    historyRef.current.push({
      pieces: piecesRef.current.map((piece) => piece.slice()),
      companion: companionRef.current?.value,
    });
    if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
    setCanUndo(true);
  }, [piecesRef, companionRef]);

  const undo = useCallback(() => {
    const snapshot = historyRef.current.pop();
    if (!snapshot) return;
    setPiecesState(snapshot.pieces);
    if (snapshot.companion !== undefined) {
      companionRef.current?.restore(snapshot.companion);
    }
    setCanUndo(historyRef.current.length > 0);
  }, [companionRef]);

  return { pieces, setPieces, pushHistory, undo, canUndo, dirty };
}
